import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import ReactDOM from "react-dom";

import {
  FiAlertCircle,
  FiBriefcase,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiEdit2,
  FiExternalLink,
  FiFile,
  FiFlag,
  FiMessageSquare,
  FiRefreshCw,
  FiTag,
  FiUser,
  FiUsers,
  FiX,
} from "react-icons/fi";

import API from "../../../../../utils/apiCalling";
import { config } from "../../../../../utils/apiUrl";

import "./TaskDetailsDrawer.css";

const FINAL_STATUSES = [
  "COMPLETED",
  "CLOSED",
  "CANCELLED",
];

const formatLabel = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map(
      (word) =>
        word.charAt(0).toUpperCase() +
        word.slice(1)
    )
    .join(" ");

const formatDate = (
  value,
  includeTime = false
) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return new Intl.DateTimeFormat(
    "en-IN",
    includeTime
      ? {
          dateStyle: "medium",
          timeStyle: "short",
        }
      : {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
  ).format(date);
};

const getStatusClass = (status) => {
  const normalized = String(
    status || ""
  )
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");

  return `task-details-status task-details-status-${normalized}`;
};

const getPriorityClass = (
  priority
) => {
  const normalized = String(
    priority || ""
  ).toLowerCase();

  return `task-details-priority task-details-priority-${normalized}`;
};

const getComputedTaskData = (
  task
) => {
  const now = new Date();

  const dueDate = task?.dueDate
    ? new Date(task.dueDate)
    : null;

  const followUpAt =
    task?.followUpAt
      ? new Date(task.followUpAt)
      : null;

  const isFinal =
    FINAL_STATUSES.includes(
      String(
        task?.status || ""
      ).toUpperCase()
    );

  return {
    isOverdue: Boolean(
      dueDate &&
        dueDate < now &&
        !isFinal
    ),

    isFollowUpDue: Boolean(
      followUpAt &&
        followUpAt <= now &&
        !isFinal
    ),
  };
};

const DetailItem = ({
  icon: Icon,
  label,
  value,
  className = "",
}) => {
  return (
    <div
      className={`task-details-item ${className}`}
    >
      <span className="task-details-item-icon">
        <Icon />
      </span>

      <div>
        <small>{label}</small>

        <strong>
          {value || "-"}
        </strong>
      </div>
    </div>
  );
};

const TaskDetailsDrawer = ({
  open,
  taskId,
  task: initialTask = null,
  onClose,
  onEdit,
  onChangeStatus,
  onAddNote,
  onRefresh,
  onOpenSource,
}) => {
  const api = useMemo(
    () => new API(),
    []
  );

  const [task, setTask] =
    useState(initialTask);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const activeTaskId =
    taskId ||
    initialTask?._id ||
    null;

  const fetchTaskDetails =
    useCallback(async () => {
      if (
        !open ||
        !activeTaskId
      ) {
        return;
      }

      try {
        setLoading(true);
        setError("");

        const response =
          await api.get(
            config.getTaskDetails(
              activeTaskId
            )
          );

        if (
          response?.status ===
          "success"
        ) {
          setTask(
            response?.data ||
              null
          );

          if (
            response?.data
              ?.hasAdminUnreadActivity
          ) {
            try {
              await api.patch(
                config.markTaskRead(
                  activeTaskId
                ),
                {}
              );

              setTask(
                (previous) => ({
                  ...previous,
                  hasAdminUnreadActivity:
                    false,
                  adminUnreadCount:
                    0,
                })
              );
            } catch (
              markReadError
            ) {
              console.error(
                "Mark task read error:",
                markReadError
              );
            }
          }
        } else {
          setError(
            response?.message ||
              "Unable to fetch task details."
          );
        }
      } catch (requestError) {
        console.error(
          "Get task details error:",
          requestError
        );

        setError(
          requestError?.response
            ?.data?.message ||
            requestError?.message ||
            "Unable to fetch task details."
        );
      } finally {
        setLoading(false);
      }
    }, [
      api,
      open,
      activeTaskId,
    ]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setTask(
      initialTask || null
    );

    fetchTaskDetails();
  }, [
    open,
    initialTask,
    fetchTaskDetails,
  ]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow =
      document.body.style
        .overflow;

    document.body.style.overflow =
      "hidden";

    const handleEscape = (
      event
    ) => {
      if (
        event.key === "Escape"
      ) {
        onClose?.();
      }
    };

    window.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [open, onClose]);

  const handleRefresh =
    async () => {
      await fetchTaskDetails();

      if (
        typeof onRefresh ===
        "function"
      ) {
        await onRefresh();
      }
    };

  if (!open) {
    return null;
  }

  const computed =
    getComputedTaskData(task);

  const assignedEmployee =
    task?.assignedTo &&
    typeof task.assignedTo ===
      "object"
      ? task.assignedTo
      : null;

  const collaborators =
    Array.isArray(
      task?.collaborators
    )
      ? task.collaborators
      : [];

  const comments =
    Array.isArray(
      task?.recentComments
    )
      ? task.recentComments
      : [];

  const activities =
    Array.isArray(
      task?.recentActivities
    )
      ? task.recentActivities
      : [];

  const attachments =
    Array.isArray(
      task?.recentAttachments
    )
      ? task.recentAttachments
      : [];

  return ReactDOM.createPortal(
    <div className="task-details-root">
      <button
        type="button"
        className="task-details-backdrop"
        onClick={onClose}
        aria-label="Close task details"
      />

      <aside
        className="task-details-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="task-details-title"
      >
        <header className="task-details-header">
          <div className="task-details-header-content">
            <span className="task-details-header-icon">
              <FiBriefcase />
            </span>

            <div>
              <small>
                TASK MANAGEMENT
              </small>

              <h2 id="task-details-title">
                Task Details
              </h2>
            </div>
          </div>

          <div className="task-details-header-actions">
            <button
              type="button"
              onClick={
                handleRefresh
              }
              disabled={loading}
              title="Refresh"
            >
              <FiRefreshCw
                className={
                  loading
                    ? "spinning"
                    : ""
                }
              />
            </button>

            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
            >
              <FiX />
            </button>
          </div>
        </header>

        <div className="task-details-body">
          {loading && !task ? (
            <div className="task-details-loading">
              <div className="task-details-spinner" />

              <p>
                Loading task details...
              </p>
            </div>
          ) : error ? (
            <div className="task-details-error">
              <FiAlertCircle />

              <div>
                <strong>
                  Unable to load task
                </strong>

                <span>
                  {error}
                </span>

                <button
                  type="button"
                  onClick={
                    handleRefresh
                  }
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : task ? (
            <>
              <section className="task-details-hero">
                <div className="task-details-code-row">
                  <span className="task-details-code">
                    {task.taskCode ||
                      "TASK"}
                  </span>

                  {task.hasAdminUnreadActivity ? (
                    <span className="task-details-new-badge">
                      New Update
                    </span>
                  ) : null}
                </div>

                <h3>
                  {task.title ||
                    "Untitled Task"}
                </h3>

                <p>
                  {task.description ||
                    "No task description available."}
                </p>

                <div className="task-details-badges">
                  <span
                    className={getPriorityClass(
                      task.priority
                    )}
                  >
                    <FiFlag />

                    {formatLabel(
                      task.priority
                    )}
                  </span>

                  <span
                    className={getStatusClass(
                      task.status
                    )}
                  >
                    {formatLabel(
                      task.status
                    )}
                  </span>

                  {computed.isOverdue ? (
                    <span className="task-details-overdue">
                      Overdue
                    </span>
                  ) : null}

                  {computed.isFollowUpDue ? (
                    <span className="task-details-follow-up-due">
                      Follow-up Due
                    </span>
                  ) : null}
                </div>
              </section>

              <section className="task-details-section">
                <div className="task-details-section-heading">
                  <FiUser />

                  <h4>
                    Assignment
                  </h4>
                </div>

                <div className="task-details-assignee">
                  <span className="task-details-avatar">
                    {assignedEmployee
                      ?.profileImage ? (
                      <img
                        src={
                          assignedEmployee
                            .profileImage
                        }
                        alt={
                          assignedEmployee
                            .name
                        }
                      />
                    ) : (
                      String(
                        assignedEmployee
                          ?.name || "E"
                      )
                        .charAt(0)
                        .toUpperCase()
                    )}
                  </span>

                  <div>
                    <small>
                      Assigned Employee
                    </small>

                    <strong>
                      {assignedEmployee
                        ?.name ||
                        "Employee unavailable"}
                    </strong>

                    <span>
                      {[
                        assignedEmployee
                          ?.employeeCode,
                        assignedEmployee
                          ?.department,
                        assignedEmployee
                          ?.designation,
                      ]
                        .filter(Boolean)
                        .join(" • ")}
                    </span>
                  </div>
                </div>

                {collaborators.length >
                0 ? (
                  <div className="task-details-collaborators">
                    <span>
                      <FiUsers />
                      Collaborators
                    </span>

                    <div>
                      {collaborators.map(
                        (
                          collaborator
                        ) => (
                          <span
                            key={
                              collaborator?._id ||
                              collaborator
                            }
                          >
                            {collaborator
                              ?.name ||
                              collaborator}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                ) : null}
              </section>

              <section className="task-details-section">
                <div className="task-details-section-heading">
                  <FiCalendar />

                  <h4>
                    Schedule
                  </h4>
                </div>

                <div className="task-details-grid">
                  <DetailItem
                    icon={FiCalendar}
                    label="Start Date"
                    value={formatDate(
                      task.startDate
                    )}
                  />

                  <DetailItem
                    icon={FiCalendar}
                    label="Due Date"
                    value={formatDate(
                      task.dueDate
                    )}
                    className={
                      computed.isOverdue
                        ? "danger"
                        : ""
                    }
                  />

                  <DetailItem
                    icon={FiClock}
                    label="Follow-up"
                    value={formatDate(
                      task.followUpAt,
                      true
                    )}
                    className={
                      computed.isFollowUpDue
                        ? "warning"
                        : ""
                    }
                  />

                  <DetailItem
                    icon={FiClock}
                    label="Estimated Hours"
                    value={`${Number(
                      task.estimatedHours ||
                        0
                    )} hrs`}
                  />

                  <DetailItem
                    icon={FiClock}
                    label="Actual Hours"
                    value={`${Number(
                      task.actualHours ||
                        0
                    )} hrs`}
                  />

                  <DetailItem
                    icon={FiTag}
                    label="Category"
                    value={formatLabel(
                      task.category
                    )}
                  />
                </div>
              </section>

              <section className="task-details-section">
                <div className="task-details-section-heading">
                  <FiCheckCircle />

                  <h4>
                    Progress
                  </h4>
                </div>

                <div className="task-details-progress-heading">
                  <span>
                    Completion
                  </span>

                  <strong>
                    {Number(
                      task.progress || 0
                    )}
                    %
                  </strong>
                </div>

                <div className="task-details-progress-bar">
                  <span
                    style={{
                      width: `${Math.min(
                        Math.max(
                          Number(
                            task.progress ||
                              0
                          ),
                          0
                        ),
                        100
                      )}%`,
                    }}
                  />
                </div>
              </section>

              {task?.source
                ?.sourceType &&
              task.source
                .sourceType !==
                "GENERAL" ? (
                <section className="task-details-section task-details-source-section">
                  <div className="task-details-section-heading">
                    <FiExternalLink />

                    <h4>
                      Linked Source
                    </h4>
                  </div>

                  <div className="task-details-source-card">
                    <div>
                      <small>
                        {formatLabel(
                          task.source
                            .sourceType
                        )}
                      </small>

                      <strong>
                        {task.source
                          .sourceTitle ||
                          "-"}
                      </strong>

                      <span>
                        {task.source
                          .sourceCode ||
                          "-"}
                      </span>
                    </div>

                    {typeof onOpenSource ===
                    "function" ? (
                      <button
                        type="button"
                        onClick={() =>
                          onOpenSource(
                            task
                          )
                        }
                      >
                        <FiExternalLink />
                        Open Source
                      </button>
                    ) : null}
                  </div>

                  {task
                    ?.customerContext
                    ?.customerName ? (
                    <div className="task-details-customer-card">
                      <FiUser />

                      <div>
                        <small>
                          Customer
                        </small>

                        <strong>
                          {
                            task
                              .customerContext
                              .customerName
                          }
                        </strong>

                        <span>
                          {[
                            task
                              .customerContext
                              .mobile,
                            task
                              .customerContext
                              .email,
                          ]
                            .filter(
                              Boolean
                            )
                            .join(" • ")}
                        </span>
                      </div>
                    </div>
                  ) : null}
                </section>
              ) : null}

              {Array.isArray(
                task.tags
              ) &&
              task.tags.length > 0 ? (
                <section className="task-details-section">
                  <div className="task-details-section-heading">
                    <FiTag />

                    <h4>Tags</h4>
                  </div>

                  <div className="task-details-tags">
                    {task.tags.map(
                      (tag) => (
                        <span key={tag}>
                          {formatLabel(
                            tag
                          )}
                        </span>
                      )
                    )}
                  </div>
                </section>
              ) : null}

              <section className="task-details-section">
                <div className="task-details-section-heading">
                  <FiMessageSquare />

                  <h4>
                    Recent Notes
                  </h4>

                  <span className="task-details-count">
                    {task?.counts
                      ?.comments ||
                      task.commentCount ||
                      0}
                  </span>
                </div>

                {comments.length > 0 ? (
                  <div className="task-details-note-list">
                    {comments.map(
                      (comment) => (
                        <article
                          key={
                            comment._id
                          }
                        >
                          <div>
                            <strong>
                              {comment.createdByName ||
                                formatLabel(
                                  comment.createdByType
                                )}
                            </strong>

                            <span>
                              {formatDate(
                                comment.createdAt,
                                true
                              )}
                            </span>
                          </div>

                          <small>
                            {formatLabel(
                              comment.commentType
                            )}
                          </small>

                          <p>
                            {
                              comment.message
                            }
                          </p>

                          {comment.followUpAt ? (
                            <span className="task-details-comment-follow-up">
                              <FiClock />
                              Follow-up:{" "}
                              {formatDate(
                                comment.followUpAt,
                                true
                              )}
                            </span>
                          ) : null}
                        </article>
                      )
                    )}
                  </div>
                ) : (
                  <p className="task-details-empty-text">
                    No notes added yet.
                  </p>
                )}
              </section>

              <section className="task-details-section">
                <div className="task-details-section-heading">
                  <FiRefreshCw />

                  <h4>
                    Recent Activity
                  </h4>

                  <span className="task-details-count">
                    {task?.counts
                      ?.activities ||
                      task.activityCount ||
                      0}
                  </span>
                </div>

                {activities.length > 0 ? (
                  <div className="task-details-activity-list">
                    {activities.map(
                      (activity) => (
                        <article
                          key={
                            activity._id
                          }
                        >
                          <span className="task-details-activity-dot" />

                          <div>
                            <strong>
                              {
                                activity.message
                              }
                            </strong>

                            <span>
                              {[
                                activity.actorName,
                                formatDate(
                                  activity.createdAt,
                                  true
                                ),
                              ]
                                .filter(
                                  Boolean
                                )
                                .join(" • ")}
                            </span>
                          </div>
                        </article>
                      )
                    )}
                  </div>
                ) : (
                  <p className="task-details-empty-text">
                    No activity available.
                  </p>
                )}
              </section>

              <section className="task-details-section">
                <div className="task-details-section-heading">
                  <FiFile />

                  <h4>
                    Attachments
                  </h4>

                  <span className="task-details-count">
                    {task?.counts
                      ?.attachments ||
                      task.attachmentCount ||
                      0}
                  </span>
                </div>

                {attachments.length >
                0 ? (
                  <div className="task-details-attachment-list">
                    {attachments.map(
                      (attachment) => (
                        <a
                          key={
                            attachment._id
                          }
                          href={
                            attachment.fileUrl
                          }
                          target="_blank"
                          rel="noreferrer"
                        >
                          <FiFile />

                          <div>
                            <strong>
                              {attachment.originalName ||
                                attachment.fileName}
                            </strong>

                            <span>
                              {formatDate(
                                attachment.createdAt,
                                true
                              )}
                            </span>
                          </div>

                          <FiExternalLink />
                        </a>
                      )
                    )}
                  </div>
                ) : (
                  <p className="task-details-empty-text">
                    No attachments uploaded.
                  </p>
                )}
              </section>

              <section className="task-details-section">
                <div className="task-details-section-heading">
                  <FiClock />

                  <h4>
                    Record Information
                  </h4>
                </div>

                <div className="task-details-grid">
                  <DetailItem
                    icon={FiCalendar}
                    label="Created On"
                    value={formatDate(
                      task.createdAt,
                      true
                    )}
                  />

                  <DetailItem
                    icon={FiCalendar}
                    label="Last Updated"
                    value={formatDate(
                      task.updatedAt,
                      true
                    )}
                  />

                  <DetailItem
                    icon={FiRefreshCw}
                    label="Latest Activity"
                    value={formatDate(
                      task.latestActivityAt,
                      true
                    )}
                  />

                  <DetailItem
                    icon={FiUser}
                    label="Created By"
                    value={
                      task
                        ?.assignedBy
                        ?.name ||
                      task
                        ?.latestActivityByName ||
                      "Admin"
                    }
                  />
                </div>
              </section>
            </>
          ) : null}
        </div>

        {task ? (
          <footer className="task-details-footer">
            <button
              type="button"
              className="task-details-note-button"
              onClick={() =>
                onAddNote?.(task)
              }
            >
              <FiMessageSquare />
              Add Note
            </button>

            <button
              type="button"
              className="task-details-status-button"
              onClick={() =>
                onChangeStatus?.(
                  task
                )
              }
            >
              <FiRefreshCw />
              Change Status
            </button>

            <button
              type="button"
              className="task-details-edit-button"
              onClick={() =>
                onEdit?.(task)
              }
            >
              <FiEdit2 />
              Edit Task
            </button>
          </footer>
        ) : null}
      </aside>
    </div>,
    document.body
  );
};

export default TaskDetailsDrawer;